/**
 * Base class for domain errors that can be mapped to HTTP responses.
 * Adapters use httpStatus and retriable to decide status code and idempotency cache.
 */
export abstract class DomainError extends Error {
    abstract readonly httpStatus: number;
    abstract readonly code: string;
    readonly retriable: boolean;

    /**
     * The fault this one was translated from, when there was one.
     *
     * Translating replaces the provider's vocabulary with ours, and `name` is overwritten
     * below - so without this a throttle reaches the logs as `SourceUnavailableError` and
     * `ProvisionedThroughputExceededException`, the string that says which limit was hit,
     * is gone. Whatever reads it has to treat it as opaque: it is the SDK's object, not
     * ours, and it is absent whenever an error was raised rather than translated.
     *
     * Assigned here rather than passed to `super(message, { cause })`. This file reaches
     * the browser bundle, and the constructor already hand-rolls `setPrototypeOf` for the
     * same downlevelling reason.
     */
    readonly cause?: unknown;

    constructor(
        message: string,
        retriable = false,
        cause?: unknown
    ) {
        super(message);
        this.name = this.constructor.name;
        Object.setPrototypeOf(this, new.target.prototype);
        this.retriable = retriable;
        this.cause = cause;
    }

    /** Alias for adapters that expect status or statusCode on the error. */
    get statusCode(): number {
        return this.httpStatus;
    }

    get status(): number {
        return this.httpStatus;
    }
}
